import { useCallback } from "react";
import { useLocation } from "react-router-dom";
import { ROUTE_NAMES, TOAST_VARIANT } from "@root/constants";
import { useNotification } from "./useNotification";
import { useTranslation } from "react-i18next";
import useProjectsDashboard from "./useProjectsDashboard";

interface UseChatSubmitProps {
  input: string;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  isSendDisabled: boolean;
  attachedFile: any[];
  currentPathname: string;
  setDisplayFiles: (files: any[]) => void;
}

/**
 * Custom hook to handle chat submission logic
 */
export const useChatSubmit = ({
  input,
  inputRef,
  isSendDisabled,
  attachedFile,
  currentPathname,
  setDisplayFiles,
}: UseChatSubmitProps) => {
  const { pathname } = useLocation();
  const { showNotification } = useNotification();
  const { t } = useTranslation("common");
  const { onSubmitHandler } = useProjectsDashboard();

  const onSubmit = useCallback(
    (onSend: (value: string) => void) => {
      if (isSendDisabled) {
        // Show warning toast for CHECKLIST when no files are selected
        if (
          currentPathname.includes(ROUTE_NAMES.CHECKLIST) &&
          attachedFile?.length === 0
        ) {
          showNotification({
            type: TOAST_VARIANT.WARNING,
            message: t("upload_document.selectFilesFirst"),
          });
        }
        return;
      }

      const inputValue = inputRef?.current?.value || "";
      if (
        pathname === ROUTE_NAMES.DRAWING_ANALYSIS_DASHBOARD &&
        !attachedFile.length
      ) {
        showNotification({
          type: TOAST_VARIANT.ERROR,
          message: t("upload_document.upload_drawing_file"),
        });
        return;
      }

      setDisplayFiles([]);

      if (
        currentPathname === ROUTE_NAMES.DOCUMENT_CHAT ||
        currentPathname === ROUTE_NAMES.DRAWING_ANALYSIS_CHAT ||
        currentPathname === ROUTE_NAMES.KSCE_CHAT_SCREEN
      ) {
        onSubmitHandler(inputValue);
        return;
      }
      onSend(inputValue);
    },
    [
      isSendDisabled,
      currentPathname,
      attachedFile,
      pathname,
      inputRef,
      showNotification,
      t,
      onSubmitHandler,
      setDisplayFiles,
    ]
  );

  return { onSubmit };
};
